/**
 * Column — Define una columna de la Table.
 *
 * Props generales:
 *   header         string      Texto del encabezado.
 *   value          string      Clave del campo en los objetos de data.
 *   body           function    Render personalizado: (rowData) => ReactNode.
 *   onClick        function    Callback al hacer click en la celda: (rowData, rowIndex) => void.
 *   sortable       boolean     Permite ordenar por esta columna.
 *   width          string      Ancho fijo (ej: "120px").
 *   align          string      "left" | "center" | "right".
 *   responsive     boolean     Candidata a ocultarse en pantallas pequeñas.
 *   tooltipHeader  string      Texto que se muestra como tooltip al hacer hover sobre el encabezado.
 *
 * Props de edición inline:
 *   edit           string      Tipo de input al editar: "text" | "checkbox" | "select" | "currency" | "date" | "datetime".
 *   editOptions    array       Opciones para edit="select".
 *                              Sin mapper: [{ label: string, value: any }]
 *                              Con mapper: cualquier array — ver editMapper.
 *   editLabelKey   string      Campo de la fila donde se guarda la etiqueta del select al guardar.
 *                              Ej: value="ID_AREA" editLabelKey="AREA" actualiza ambos automáticamente.
 *   editMapper     object      Mapeo de campos cuando editOptions no usa { label, value }.
 *                              Formato: { name: 'campoNombre', value: 'campoValor' }
 *                              Ej: editMapper={{ name: 'name', value: 'code' }}
 *   editDisabled   bool|fn     Deshabilita la celda durante la edición.
 *                              Como función recibe (rowData, draftValues, rowIndex) => bool.
 *                              Útil para selects dependientes: editDisabled={(row, draft) => !draft.campoParent}
 *   editCondition  function    (rowData) => boolean. Si retorna false, la celda no es editable.
 *
 * Callbacks de la Table relacionados:
 *   onRowEdit(newRow, rowIndex, originalRow)  Llamado al guardar. Si retorna false la edición no se cierra.
 *   onDraftChange(field, value, draftActual)  Llamado cada vez que cambia un campo en edición.
 *                                             Puede retornar { campo: valor } para sobrescribir otros drafts
 *                                             (útil para limpiar campos dependientes).
 *   onEditStart(row, rowIndex, draftInicial)  Llamado al abrir la edición de una fila.
 *                                             Útil para pre-cargar opciones dependientes.
 *
 * ─────────────────────────────────────────────────────────────────
 * Ejemplo select simple (copiar, pegar y reemplazar campos):
 *
 *   // 1. Preparar las opciones como [{ value, label }]
 *   const opcionesEstatus = data.estatus.map(e => ({ value: e.ID, label: e.NOMBRE }));
 *
 *   // 2. Declarar la columna — editLabelKey sincroniza la etiqueta automáticamente al guardar
 *   <Column
 *       header="ESTATUS"
 *       value="ID_ESTATUS"       // campo numérico que se guarda en la fila (FK / ID)
 *       editLabelKey="ESTATUS"   // campo texto que muestra la etiqueta en la fila
 *       edit="select"
 *       body={(row) => row.ESTATUS}
 *       editOptions={opcionesEstatus}
 *   />
 *
 *   // 3. handleRowEdit no necesita resolver etiquetas — newRow ya las trae correctas
 *   const handleRowEdit = (newRow, rowIndex) => {
 *       setData(prev => prev.map((item, i) => i === rowIndex ? newRow : item));
 *       api.update({ id: newRow.ID, estatus_id: newRow.ID_ESTATUS });
 *   };
 *
 * ─────────────────────────────────────────────────────────────────
 * Ejemplo select dependiente (el segundo select depende del primero):
 *
 *   // 1. Opciones del select padre siempre disponibles; hijas se cargan al abrir la fila
 *   const [opcionesTipo]       = useState(data.tipos.map(t => ({ value: t.ID, label: t.NOMBRE })));
 *   const [opcionesSubtipo, setOpcionesSubtipo] = useState([]);
 *
 *   // 2. Columnas — la hija se deshabilita si el padre está vacío
 *   <Column
 *       header="TIPO"
 *       value="ID_TIPO"
 *       editLabelKey="TIPO"
 *       edit="select"
 *       body={(row) => row.TIPO}
 *       editOptions={opcionesTipo}
 *   />
 *   <Column
 *       header="SUBTIPO"
 *       value="ID_SUBTIPO"
 *       editLabelKey="SUBTIPO"
 *       edit="select"
 *       body={(row) => row.SUBTIPO}
 *       editOptions={opcionesSubtipo}
 *       editDisabled={(row, draft) => !draft.ID_TIPO}
 *   />
 *
 *   // 3. Cargar opciones hijas al abrir edición y limpiarlas al cambiar el padre
 *   const handleEditStart = (row) => {
 *       api.getSubtipos(row.ID_TIPO).then(res =>
 *           setOpcionesSubtipo(res.map(s => ({ value: s.ID, label: s.NOMBRE })))
 *       );
 *   };
 *   const handleDraftChange = (field, value) => {
 *       if (field === 'ID_TIPO') {
 *           api.getSubtipos(value).then(res =>
 *               setOpcionesSubtipo(res.map(s => ({ value: s.ID, label: s.NOMBRE })))
 *           );
 *           return { ID_SUBTIPO: null, SUBTIPO: '' };
 *       }
 *   };
 *
 *   <Table onEditStart={handleEditStart} onDraftChange={handleDraftChange} onRowEdit={handleRowEdit} ...>
 */

const Column = () => null;

export default Column;
